// Product catalog, Burly Fine Fireclay Sinks
// Photography supplied by the client. Edit freely.

import {
  LIFESTYLE_KITCHEN_NAVY,
  PRODUCT_FLUTED_BRONZE,
  PRODUCT_DOUBLE_BOWL_POTS,
  PRODUCT_UTILITY_CYLINDERS,
} from "@/data/site";

export const CATEGORIES = [
  { slug: "farmhouse", name: "Farmhouse Apron", blurb: "Signature apron-front fireclay sinks for the modern country kitchen." },
  { slug: "butler", name: "Butler Sinks", blurb: "Classic rectangular butler and Belfast fireclay sinks, the Victorian original, refined." },
  { slug: "undermount", name: "Undermount", blurb: "Seamless undermount installation for sleek, minimalist countertops." },
  { slug: "topmount", name: "Topmount", blurb: "Classic drop-in fireclay sinks built for daily resilience." },
  { slug: "bar-prep", name: "Bar & Prep", blurb: "Compact fireclay basins designed for islands, bars, and prep stations." },
  { slug: "bathroom", name: "Bathroom Sinks", blurb: "Hand-finished fireclay basins for premium bathroom suites." },
];

export const FINISHES = [
  { id: "matte-bone", name: "Matte Bone", hex: "#F0EBE1" },
  { id: "gloss-alabaster", name: "Gloss Alabaster", hex: "#FAF7F1" },
  { id: "stone-grey", name: "Stone Grey", hex: "#B7B3AA" },
  { id: "fired-charcoal", name: "Fired Charcoal", hex: "#2E2C29" },
  { id: "clay-terracotta", name: "Clay Terracotta", hex: "#B85D3B" },
  { id: "midnight-cobalt", name: "Midnight Cobalt", hex: "#1F3344" },
];

// Generic placeholders for products we don't yet have photography for.
const PLACEHOLDER_BATHROOM_1 =
  "https://images.unsplash.com/photo-1576698483491-8c43f0862543?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const PLACEHOLDER_BATHROOM_2 =
  "https://images.unsplash.com/photo-1642755623069-eb720c326553?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const PRODUCTS = [
  {
    slug: "burly-shaker-30",
    name: "Shaker 30",
    category: "farmhouse",
    tagline: "30-inch single bowl apron-front",
    price_band: "Premium",
    dimensions: { length_in: 30, width_in: 18, depth_in: 10 },
    weight_kg: 47,
    bowls: 1,
    drainage: "Center rear",
    finishes: ["matte-bone", "gloss-alabaster", "fired-charcoal"],
    description:
      "A 30-inch single-bowl apron-front sink fired at 1,250°C for vitreous strength. The Shaker 30 is the workhorse of any heritage kitchen, deep enough to bathe a stockpot, gentle enough for porcelain.",
    features: [
      "Hand-finished apron front",
      "Vitreous glaze, resistant to acid and stain",
      "Heat resistant up to 530°C",
      "10-year limited warranty",
    ],
    images: [LIFESTYLE_KITCHEN_NAVY, PRODUCT_FLUTED_BRONZE],
  },
  {
    slug: "burly-shaker-36",
    name: "Shaker 36",
    category: "farmhouse",
    tagline: "36-inch single bowl apron-front",
    price_band: "Premium",
    dimensions: { length_in: 36, width_in: 19, depth_in: 10 },
    weight_kg: 58,
    bowls: 1,
    drainage: "Center rear",
    finishes: ["matte-bone", "gloss-alabaster", "stone-grey"],
    description:
      "Our largest single-bowl apron-front fireclay sink. Sized for kitchens that cook, the Shaker 36 carries a deep, generous bowl with a softly chamfered apron.",
    features: [
      "Solid 12mm fireclay wall",
      "Reversible apron, flat or fluted",
      "Non-porous glaze, easy to clean",
      "Compatible with all standard 3.5\" disposers",
    ],
    images: [PRODUCT_FLUTED_BRONZE, LIFESTYLE_KITCHEN_NAVY],
  },
  {
    slug: "burly-mason-33-double",
    name: "Mason 33 Double",
    category: "farmhouse",
    tagline: "33-inch double bowl apron-front",
    price_band: "Premium",
    dimensions: { length_in: 33, width_in: 18, depth_in: 9.5 },
    weight_kg: 54,
    bowls: 2,
    drainage: "Twin rear",
    finishes: ["matte-bone", "gloss-alabaster", "fired-charcoal", "stone-grey"],
    description:
      "A symmetrical double-bowl fireclay apron-front sink for two-handed kitchens. The Mason 33 keeps prep and wash worlds neatly apart.",
    features: [
      "Equal 50/50 bowl split",
      "Twin drains for parallel use",
      "Fluted apron option available",
      "Suitable for granite, quartz, and timber counters",
    ],
    images: [PRODUCT_DOUBLE_BOWL_POTS, PRODUCT_UTILITY_CYLINDERS],
  },
  {
    slug: "burly-belfast-24",
    name: "Belfast 24",
    category: "butler",
    tagline: "24-inch single-bowl Belfast butler sink",
    price_band: "Premium",
    dimensions: { length_in: 24, width_in: 18, depth_in: 10 },
    weight_kg: 41,
    bowls: 1,
    drainage: "Center rear with weir overflow",
    finishes: ["matte-bone", "gloss-alabaster"],
    description:
      "The Belfast 24 is the Victorian original, a deep, rectangular butler sink with a built-in weir overflow. Crafted in fine fireclay and finished by hand, it carries the same silhouette specified in English servants' pantries since the 1800s.",
    features: [
      "Traditional weir overflow",
      "Hand-finished rim",
      "Below-counter or in-frame installation",
      "Pairs with timber or stone counters",
    ],
    images: [PRODUCT_FLUTED_BRONZE, LIFESTYLE_KITCHEN_NAVY],
  },
  {
    slug: "burly-butler-30-london",
    name: "London Butler 30",
    category: "butler",
    tagline: "30-inch rectangular butler sink",
    price_band: "Premium",
    dimensions: { length_in: 30, width_in: 18, depth_in: 9 },
    weight_kg: 50,
    bowls: 1,
    drainage: "Center",
    finishes: ["matte-bone", "gloss-alabaster", "stone-grey"],
    description:
      "A larger, undecorated butler sink for serious kitchens. The London Butler 30 follows the traditional London-pattern proportions, wider and shallower than a Belfast, without an overflow.",
    features: [
      "No overflow, full prep depth",
      "Reinforced base for in-frame installation",
      "Compatible with three-hole bridge taps",
      "Templates included for cabinet makers",
    ],
    images: [PRODUCT_UTILITY_CYLINDERS, PRODUCT_FLUTED_BRONZE],
  },
  {
    slug: "burly-quarry-24",
    name: "Quarry 24",
    category: "undermount",
    tagline: "24-inch undermount single bowl",
    price_band: "Standard",
    dimensions: { length_in: 24, width_in: 17, depth_in: 9 },
    weight_kg: 32,
    bowls: 1,
    drainage: "Center",
    finishes: ["matte-bone", "gloss-alabaster", "stone-grey", "midnight-cobalt"],
    description:
      "A clean-edged undermount fireclay sink for stone and engineered countertops. The Quarry 24 disappears into the counter, leaving the material to speak.",
    features: [
      "Precision-trimmed rim, ±0.5mm tolerance",
      "Hidden mounting profile",
      "Sound-dampened underside coating",
      "Drop-in compatible with template kit",
    ],
    images: [PRODUCT_UTILITY_CYLINDERS, PRODUCT_DOUBLE_BOWL_POTS],
  },
  {
    slug: "burly-foundry-30-topmount",
    name: "Foundry 30 Topmount",
    category: "topmount",
    tagline: "30-inch drop-in fireclay",
    price_band: "Standard",
    dimensions: { length_in: 30, width_in: 18, depth_in: 9 },
    weight_kg: 38,
    bowls: 1,
    drainage: "Center rear",
    finishes: ["matte-bone", "gloss-alabaster", "stone-grey", "fired-charcoal"],
    description:
      "A drop-in fireclay sink for retrofit-friendly installations. Foundry 30 delivers the same vitreous body and depth without the renovation overhead.",
    features: [
      "Engineered self-rimming edge",
      "Three pre-drilled tap holes (configurable)",
      "Quick-fit clip system",
      "Pairs with all Burly accessory rails",
    ],
    images: [LIFESTYLE_KITCHEN_NAVY, PRODUCT_FLUTED_BRONZE],
  },
  {
    slug: "burly-tinker-18-bar",
    name: "Tinker 18 Bar",
    category: "bar-prep",
    tagline: "18-inch compact bar & prep sink",
    price_band: "Standard",
    dimensions: { length_in: 18, width_in: 16, depth_in: 8 },
    weight_kg: 22,
    bowls: 1,
    drainage: "Center",
    finishes: ["matte-bone", "gloss-alabaster", "clay-terracotta", "fired-charcoal"],
    description:
      "Compact and considered. The Tinker 18 is built for the island corner, the home bar, and the prep wing, small footprint, full fireclay character.",
    features: [
      "Built-in accessory ledge",
      "Splash-bevel rim",
      "Available in six finishes",
      "Templates included for stone fabricators",
    ],
    images: [PRODUCT_UTILITY_CYLINDERS, PRODUCT_FLUTED_BRONZE],
  },
  {
    slug: "burly-vessel-bath-oval",
    name: "Vessel Bath Oval",
    category: "bathroom",
    tagline: "Hand-finished fireclay vessel basin",
    price_band: "Premium",
    dimensions: { length_in: 22, width_in: 15, depth_in: 5.5 },
    weight_kg: 14,
    bowls: 1,
    drainage: "Center",
    finishes: ["gloss-alabaster", "matte-bone", "midnight-cobalt", "clay-terracotta"],
    description:
      "A hand-finished oval vessel basin in fireclay. Sits atop the counter as both fixture and sculpture, finished in our editorial palette.",
    features: [
      "Vessel-style countertop install",
      "Hand-trimmed rim",
      "Compatible with universal pop-up drains",
      "Available with matching fireclay pedestal",
    ],
    images: [PLACEHOLDER_BATHROOM_1, PLACEHOLDER_BATHROOM_2],
  },
  {
    slug: "burly-plinth-bath-rect",
    name: "Plinth Bath Rect",
    category: "bathroom",
    tagline: "Architectural rectangular basin",
    price_band: "Premium",
    dimensions: { length_in: 24, width_in: 14, depth_in: 6 },
    weight_kg: 17,
    bowls: 1,
    drainage: "Rear",
    finishes: ["gloss-alabaster", "matte-bone", "stone-grey", "fired-charcoal"],
    description:
      "A precise rectangular fireclay basin for architecturally-minded bathrooms. Sharp edges, soft interior radii.",
    features: [
      "Counter-recessed or vessel install",
      "Concealed overflow",
      "1.2mm crisp glaze edge",
      "Suitable for hospitality projects",
    ],
    images: [PLACEHOLDER_BATHROOM_2, PLACEHOLDER_BATHROOM_1],
  },
];

export const findProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);
export const productsByCategory = (catSlug) =>
  PRODUCTS.filter((p) => p.category === catSlug);
export const findFinish = (id) => FINISHES.find((f) => f.id === id);
